import { useState, useEffect } from 'react';
import { Clock, Bell, Flame } from 'lucide-react';

export function Timer() {
  const [timeLeft, setTimeLeft] = useState(() => {
    try {
      const saved = localStorage.getItem('timer');
      if (saved) {
        const parsed = JSON.parse(saved);
        const now = new Date().getTime();
        const diff = Math.max(0, now - parsed.lastUpdate);
        const secondsPassed = Math.floor(diff / 1000);
        
        let { hours, minutes, seconds } = parsed.time;
        
        // Convert all to seconds
        let totalSeconds = hours * 3600 + minutes * 60 + seconds;
        
        // Subtract elapsed time
        totalSeconds = Math.max(0, totalSeconds - secondsPassed);
        
        // If expired or invalid, reset to 24 hours
        if (totalSeconds <= 0 || isNaN(totalSeconds)) {
          return { hours: 23, minutes: 59, seconds: 59 };
        }
        
        // Convert back to hours, minutes, seconds
        hours = Math.floor(totalSeconds / 3600);
        minutes = Math.floor((totalSeconds % 3600) / 60);
        seconds = totalSeconds % 60;
        
        return { hours, minutes, seconds };
      }
    } catch (error) {
      console.error('Error parsing timer:', error);
    }
    // Default or error case: return 24 hours
    return { hours: 23, minutes: 59, seconds: 59 };
  });

  const [stockLeft] = useState(() => {
    try {
      const saved = localStorage.getItem('stockLeft');
      const parsed = parseInt(saved || '');
      if (!isNaN(parsed) && parsed > 0) {
        return parsed;
      }
    } catch (error) {
      console.error('Error parsing stock:', error);
    }
    return Math.floor(Math.random() * 10) + 5;
  });

  useEffect(() => {
    const timer = setInterval(() => {
      setTimeLeft(prev => {
        try {
          const newTime = { ...prev };
          if (newTime.seconds > 0) {
            newTime.seconds--;
          } else if (newTime.minutes > 0) {
            newTime.minutes--;
            newTime.seconds = 59;
          } else if (newTime.hours > 0) {
            newTime.hours--;
            newTime.minutes = 59;
            newTime.seconds = 59;
          } else {
            // Reset to 24 hours when timer reaches 0
            newTime.hours = 23;
            newTime.minutes = 59;
            newTime.seconds = 59;
          }
          
          // Save to localStorage with error handling
          try {
            localStorage.setItem('timer', JSON.stringify({
              time: newTime,
              lastUpdate: new Date().getTime()
            }));
          } catch (error) {
            console.error('Error saving timer:', error);
          }
          
          return newTime;
        } catch (error) {
          console.error('Error updating timer:', error);
          // Return default state in case of error
          return { hours: 23, minutes: 59, seconds: 59 };
        }
      });
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Save stock left with error handling
  useEffect(() => {
    try {
      localStorage.setItem('stockLeft', stockLeft.toString());
    } catch (error) {
      console.error('Error saving stock:', error);
    }
  }, [stockLeft]);

  return (
    <div className="bg-gradient-to-br from-red-900/20 to-black/40 backdrop-blur-sm rounded-xl border border-red-500/20 p-4 sm:p-6 mb-6 sm:mb-8 transform transition-all duration-500 hover:border-red-500/40 hover:shadow-[0_0_30px_rgba(239,68,68,0.2)]">
      <div className="space-y-3 mb-4">
        <div className="flex items-center justify-center gap-2">
          <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
          <div className="flex items-center gap-2">
            <Bell className="w-5 h-5 text-red-500 animate-bounce" />
            <p className="text-red-500 font-semibold text-sm sm:text-base">Ofertă de lansare - Preț special</p>
            <Flame className="w-5 h-5 text-red-500 animate-bounce" />
          </div>
        </div>
        <div className="flex items-center justify-center gap-2 text-sm">
          <span className="text-gray-400 line-through">89.99 RON</span>
          <span className="text-white font-bold">60 RON</span>
          <span className="bg-red-500 text-white text-xs px-2 py-0.5 rounded-full">-33%</span>
        </div>
      </div>
      
      <div className="flex items-center justify-center gap-3 sm:gap-4">
        <div className="text-center">
          <div className="bg-gradient-to-b from-red-900/40 to-black/60 rounded-lg p-2 sm:p-3 min-w-[50px] sm:min-w-[60px] border border-red-500/10">
            <span className="text-xl sm:text-2xl md:text-3xl font-bold text-white">{timeLeft.hours.toString().padStart(2, '0')}</span>
          </div>
          <p className="text-xs sm:text-sm text-gray-400 mt-1">Ore</p>
        </div>
        <span className="text-xl sm:text-2xl md:text-3xl font-bold text-red-500">:</span>
        <div className="text-center">
          <div className="bg-gradient-to-b from-red-900/40 to-black/60 rounded-lg p-2 sm:p-3 min-w-[50px] sm:min-w-[60px] border border-red-500/10">
            <span className="text-xl sm:text-2xl md:text-3xl font-bold text-white">{timeLeft.minutes.toString().padStart(2, '0')}</span>
          </div>
          <p className="text-xs sm:text-sm text-gray-400 mt-1">Minute</p>
        </div>
        <span className="text-xl sm:text-2xl md:text-3xl font-bold text-red-500">:</span>
        <div className="text-center">
          <div className="bg-gradient-to-b from-red-900/40 to-black/60 rounded-lg p-2 sm:p-3 min-w-[50px] sm:min-w-[60px] border border-red-500/10">
            <span className="text-xl sm:text-2xl md:text-3xl font-bold text-white">{timeLeft.seconds.toString().padStart(2, '0')}</span>
          </div>
          <p className="text-xs sm:text-sm text-gray-400 mt-1">Secunde</p>
        </div>
      </div>

      <div className="mt-4 space-y-3">
        <div className="text-center">
          <p className="text-sm sm:text-base text-gray-300">
            <span className="text-red-500 font-semibold animate-pulse">Stoc limitat</span> la acest preț special de lansare!
          </p>
        </div>

        <div className="flex flex-col items-center gap-2 text-sm text-gray-400">
          <div className="flex items-center gap-2">
            <Clock className="w-4 h-4 text-red-500" />
            <p>Livrare rapidă garantată în 24-48h</p>
          </div>
          <div className="flex items-center gap-2">
            <Flame className="w-4 h-4 text-red-500" />
            <p>Garanție de returnare 30 zile</p>
          </div>
        </div>
      </div>
    </div>
  );
}
